<template>
  <v-card>
    <v-card-title class="primary">
      <span class="headline white--text">
        {{ ticketBook?.name }}
      </span>
      <v-spacer/>
      <Icon
      iconColor="white"
      icon="mdi-close"
      :small="false"
      large
      :tooltipText="$t('close')"
      @icon-clicked="$emit('close')"/>
    </v-card-title>
    <v-card-text>
      <v-row class="mt-2">
        <v-col>
          <v-text-field
            v-model.number="goToPageNumber"
            hide-details
            dense
            outlined
            :label="$t('goToPage')"
            type="number"
            append-outer-icon="mdi-magnify"
            @click:append-outer="goToPage"
            @keyup.enter="goToPage"
            hide-spin-buttons
            clearable
          />
        </v-col>
      </v-row>

      <v-row class="mt-1">
        <v-col>
          <v-data-table
          :headers="headers"
          dense
          :items="pages"
          :page.sync="tablePage"
          :items-per-page.sync="itemsPerPage"
          :footer-props="{ 'items-per-page-options': TicketBookConstants.ItemsPerPageOptions }">
            <template #body="{ items }">
              <tbody>
                <tr
                  v-for="(item, index) in items"
                  :key="item.pageNumber"
                  :class="{
                    'highlight-below': (index >= hoveredIndex || index >= focusedIndex || item.pageNumber === goToPageNumber)
                  }"
                >
                  <td class="text-left" style="max-width:80px; width:1%; white-space:nowrap;">
                    <slot name="item.pageNumber" :item="item">
                      {{ item.pageNumber }}
                    </slot>
                  </td>
                  <td class="text-right">
                    <slot name="item.ticketNumber" :item="item">
                      <a
                      v-if="item.ticketId"
                      class="primary--text text-decoration-underline"
                      @click.stop="viewTicket(item.ticketId)">{{ item.ticketNumber }}</a>
                      <span v-else>{{ item.ticketNumber }}</span>
                    </slot>
                  </td>
                  <td class="text-center" v-if="!readonly">
                    <slot name="item.actions" :item="item" :index="index">
                      <Icon
                        @mouseenter.native="hoveredIndex = index"
                        @mouseleave.native="hoveredIndex = undefined"
                        @icon-clicked="splitBook(item, index)"
                        icon="mdi-call-split"
                        :tooltipText="$t('splitTicketBook')"
                        :iconColor="(index >= hoveredIndex || index >= focusedIndex) ? 'white' : 'primary'"
                      />
                    </slot>
                  </td>
                </tr>
              </tbody>
            </template>
            <template #header.pageNumber>
              <span style="white-space: nowrap;">
                {{ $t('pageNumber') }}
              </span>
            </template>
          </v-data-table>
        </v-col>
      </v-row>
    </v-card-text>
    <v-dialog v-model="confirmSplitDialog" max-width="500px">
      <TicketBookSplitDialog
        v-if="confirmSplitDialog"
        :body="splitPageDialogBody"
        :accountId="accountId"
        @confirm="confirmSplitTicketBook"
        @cancel="confirmSplitDialog = false"
      />
    </v-dialog>
    <Dialog :stateId="ticketDetailDialogId" maxWidth="80vw" @close="resetTicketDetailDialog">
      <TicketDetails
      v-if="ticketDetailDialog"
      :ticketId="focusedTicketId"/>
    </Dialog>
  </v-card>
</template>
<script>
import { mapActions } from 'vuex'
import { ticketBookPageHeaders } from '@/headers/Account'
import { uniqueDialogId } from '@/utils/componentHelpers'
import { TicketBookConstants } from '@/utils/constants.js'

export default {
  name: 'TicketBookDetail',

  components: {
    Icon: () => import('@/components/helper/Icon.vue'),
    TicketBookSplitDialog: () => import('@/components/account/ticketBooks/TicketBookSplitDialog.vue'),
    Dialog: () => import('@/components/Dialog.vue'),
    TicketDetails: () => import('@/components/ticket/ticket-details/TicketDetails.vue')
  },

  props: {
    ticketBook: {
      type: Object,
      required: true
    },

    readonly: {
      Type: Boolean,
      required: true
    },

    accountId: {
      type: Number,
      default: undefined
    },

    initialPageNumber: {
      type: Number,
      default: undefined
    }
  },

  data: () => ({
    TicketBookConstants,
    pages: [],
    hoveredIndex: undefined,
    confirmSplitDialog: false,
    splitPages: [],
    focusedIndex: undefined,
    goToPageNumber: undefined,
    tablePage: 1,
    itemsPerPage: 10,
    ticketDetailDialogId: uniqueDialogId('ticket-book-ticket-detail'),
    ticketDetailDialog: false,
    focusedTicketId: undefined
  }),

  watch: {
    confirmSplitDialog (v) {
      if (v) return
      this.closeDialogs()
    }
  },

  computed: {
    headers () {
      return ticketBookPageHeaders(!this.readonly)
    },

    sortedPages () {
      return this.pages.toSorted((a, b) => a.pageNumber - b.pageNumber)
    },

    splitPageDialogBody () {
      return this.splitPages.length === 1
        ? this.$t('splitTicketBookSinglePage', { pageNumber: this.splitPages[0].pageNumber })
        : this.$t('splitTicketBookMultiplePages', {
          pageCount: this.splitPages.length,
          start: this.splitPages[0].pageNumber,
          end: this.splitPages[this.splitPages.length - 1].pageNumber
        })
    }
  },

  async created () {
    if (this.ticketBook?.pages?.length > 0) {
      this.pages = this.ticketBook.pages
    } else {
      this.pages = await this.fetchTicketBookPages({
        accountId: this.accountId,
        ticketBookId: this.ticketBook.ticketBookId
      })
    }
    this.pages.sort((p1, p2) => p1.pageNumber - p2.pageNumber)

    if (this.initialPageNumber !== undefined) {
      this.goToPageNumber = this.initialPageNumber
      this.goToPage()
    }
  },

  methods: {
    ...mapActions('account', ['fetchTicketBookPages']),
    ...mapActions('dialog', ['openOrUpdateDialog']),

    viewTicket (ticketId) {
      this.focusedTicketId = ticketId
      this.ticketDetailDialog = true
      this.openOrUpdateDialog({ id: this.ticketDetailDialogId, allowFullscreen: true })
    },

    resetTicketDetailDialog () {
      this.ticketDetailDialog = false
      this.focusedTicketId = undefined
    },

    goToPage () {
      if (!this.goToPageNumber) return
      const absIndex = this.sortedPages.findIndex(p => p.pageNumber === this.goToPageNumber)
      if (absIndex === -1) {
        this.setSnackError(this.$t('pageNotFound'))
        return
      }
      this.tablePage = Math.floor(absIndex / this.itemsPerPage) + 1
    },
    splitBook (item, tablePageIndex) {
      const ticketBookPageIndex = this.sortedPages.findIndex(p => p.pageNumber === item.pageNumber)

      if (ticketBookPageIndex === 0) {
        this.setSnackError(this.$t('cannotSplitFirstPage'))
        return
      }

      this.splitPages = this.sortedPages.slice(ticketBookPageIndex)
      const invalidPage = this.splitPages.find(p => p.ticketNumber != null)
      if (invalidPage != null) {
        this.setSnackError(this.$t('cannotSplitTicketBookWithUsedPages', { pageNumber: invalidPage.pageNumber }))
        return
      }

      this.confirmSplitDialog = true
      this.focusedIndex = tablePageIndex
    },

    confirmSplitTicketBook ({ name, targetAccountId, tractId, contractId } = {}) {
      this.confirmSplitDialog = false
      this.$emit('split-ticket-book', {
        name,
        pages: this.splitPages,
        parentTicketBookId: this.ticketBook.ticketBookId,
        targetAccountId,
        tractId,
        contractId
      })
      this.splitPages = []
    },

    closeDialogs () {
      this.confirmSplitDialog = false
      this.focusedIndex = undefined
    }
  }
}
</script>
<style scoped>
.highlight-below {
  background-color: #94A7ff !important;
}
</style>
